// The step on one nominal axis: day 0 (the step date) → the Wait bar → a pin per Then task at its offset. Units are labelled, never converted.
import { DAY_BASIS_SHORT, dueText } from "./timelineHelp";

const unitOf = (basis) => (!basis || basis === "calendar" ? "" : ` ${DAY_BASIS_SHORT[basis]}`);
const shift = (x) => (x < 6 ? "" : x > 94 ? "-translate-x-full" : "-translate-x-1/2");

function Tick({ x, label, strong, flag, testid }) {
  return (
    <div className="absolute top-0 h-full" style={{ left: `${x}%` }} data-testid={testid}>
      <div className={`absolute top-[11px] w-px h-[7px] ${strong ? "bg-slate-500" : "bg-slate-400"}`} />
      <div className={`absolute top-0 whitespace-nowrap ${shift(x)} ${strong ? "text-slate-600 font-medium" : ""}`}>{label}{flag ? <span className="text-slate-400"> · deadline</span> : null}</div>
    </div>
  );
}

function Pin({ x, label, title, floating, testid }) {
  return (
    <div className="absolute top-0 h-full" style={{ left: `${x}%` }} title={title} data-testid={testid}>
      <div className={`absolute top-[11px] -translate-x-1/2 w-[7px] h-[7px] rounded-full border ${floating ? "bg-white border-amber-500 border-dashed" : "bg-rose-600 border-rose-700"}`} />
      <div className={`absolute top-[21px] whitespace-nowrap ${shift(x)} ${floating ? "text-amber-800" : "text-rose-800"}`}>{label}</div>
    </div>
  );
}

// group the fixed pins that share a day so their labels do not overprint
function groupPins(actions, period) {
  const fixed = new Map(), floating = [];
  actions.forEach((a, i) => {
    if (a.due_mode === "before_date") { floating.push({ i, a }); return; }
    const atEnd = a.due_mode === "period_end" && period?.days;
    const day = atEnd ? Number(period.days) : Number(a.due_days || 0);
    const basis = atEnd ? period.basis : a.day_basis || "calendar";
    const key = `${day}|${basis}`;
    if (!fixed.has(key)) fixed.set(key, { day, basis, items: [] });
    fixed.get(key).items.push({ i, a });
  });
  return { fixed: [...fixed.values()], floating };
}

export function StepRuler({ period, actions = [], deadline = false, testid }) {
  const days = period?.days ? Number(period.days) : 0;
  if (!days && !actions.length) return null;
  const { fixed, floating } = groupPins(actions, period);
  const max = Math.max(days, ...fixed.map((p) => p.day), 1);
  const span = floating.length ? (days || fixed.length ? 70 : 0) : 100;
  const x = (d) => Math.min((d / max) * span, span);
  // 'before a date' pins hang off the right-hand end, the nearest to the date first
  const hanging = [...floating].sort((p, q) => Number(p.a.due_days || 0) - Number(q.a.due_days || 0));
  const refName = hanging[0]?.a.due_ref_label || "Hearing date";
  return (
    <div className="relative h-[34px] mt-2 mb-0.5 max-w-xl text-[9px] text-slate-500 select-none" data-testid={testid}>
      {span > 0 && <div className="absolute top-[14px] h-px bg-slate-300" style={{ left: 0, width: `${span}%` }} />}
      {floating.length > 0 && <div className="absolute top-[14px] h-px border-t border-dashed border-amber-300" style={{ left: `${span}%`, right: 0 }} />}
      {days > 0 && <div className="absolute top-[11px] h-[7px] bg-amber-200/80 border border-amber-300 rounded-sm" style={{ left: 0, width: `${x(days)}%` }} title={`Wait ${days}${unitOf(period.basis) || " days"}`} data-testid={testid ? `${testid}-wait` : undefined} />}
      <Tick x={0} label="0" strong />
      {days > 0 && <Tick x={x(days)} label={`${days}${unitOf(period.basis)}`} flag={deadline} testid={testid ? `${testid}-end` : undefined} />}
      {floating.length > 0 && <Tick x={100} label={refName} strong testid={testid ? `${testid}-ref` : undefined} />}
      {fixed.map((p) => (
        <Pin key={`${p.day}|${p.basis}`} x={x(p.day)} label={`${p.basis === "calendar" || !p.basis ? "day " : "+"}${p.day}${unitOf(p.basis)}${p.items.length > 1 ? ` · ${p.items.length} tasks` : ""}`}
          title={p.items.map(({ a }) => `${a.label} — due ${dueText(a, period)}`).join("\n")} testid={testid ? `${testid}-pin-${p.items[0].i}` : undefined} />
      ))}
      {hanging.map(({ i, a }, k) => (
        <Pin key={i} x={Math.max(span + 4, 100 - 8 * (k + 1))} floating label={`− ${a.due_days || 0}${unitOf(a.day_basis) || " d"}`} title={`${a.label} — due ${dueText(a, period)}`} testid={testid ? `${testid}-pin-${i}` : undefined} />
      ))}
    </div>
  );
}
